import React, { useState } from 'react';
import { FileOutput, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { costingImportsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'AED', 'SGD'];

export const CostingSheetQuotationDialog = ({ record, open, onOpenChange, onRefresh }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    currency: 'INR', exchange_rate: 1, validity_days: 30, tax_percentage: 0,
    payment_terms: '', delivery_terms: '', terms_conditions: '',
  });
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async () => {
    setSaving(true);
    try {
      const response = await costingImportsAPI.createQuotation(record.id, {
        currency: form.currency,
        exchange_rate: Number(form.exchange_rate) || 1,
        validity_days: Number(form.validity_days) || 30,
        tax_percentage: Number(form.tax_percentage) || 0,
        payment_terms: form.payment_terms || undefined,
        delivery_terms: form.delivery_terms || undefined,
        terms_conditions: form.terms_conditions || undefined,
      });
      toast.success(`Draft quotation ${response.data.quotation_number} created`, {
        description: `${response.data.items} line item(s) priced from the approved costings.`,
      });
      onOpenChange(false);
      if (onRefresh) await onRefresh();
      navigate(`/quotations/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the quotation draft');
    } finally { setSaving(false); }
  };

  if (!record) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="costing-sheet-quotation-dialog">
        <DialogHeader>
          <DialogTitle>Create quotation draft</DialogTitle>
          <DialogDescription>Every approved costing row becomes a quotation line in your standard format.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Currency</Label>
            <select className="mt-1 h-10 w-full border bg-white px-3 text-sm" value={form.currency}
              onChange={(event) => setField('currency', event.target.value)} data-testid="quotation-currency-select">
              {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
          <div>
            <Label>Exchange rate (INR per 1 {form.currency})</Label>
            <Input type="number" step="0.0001" min="0.0001" value={form.exchange_rate}
              onChange={(event) => setField('exchange_rate', event.target.value)} data-testid="quotation-exchange-rate-input" />
          </div>
          <div>
            <Label>Validity (days)</Label>
            <Input type="number" min="1" value={form.validity_days}
              onChange={(event) => setField('validity_days', event.target.value)} data-testid="quotation-validity-input" />
          </div>
          <div>
            <Label>Tax %</Label>
            <Input type="number" min="0" step="0.01" value={form.tax_percentage}
              onChange={(event) => setField('tax_percentage', event.target.value)} data-testid="quotation-tax-input" />
          </div>
          <div>
            <Label>Payment terms</Label>
            <Input value={form.payment_terms} onChange={(event) => setField('payment_terms', event.target.value)}
              placeholder="Customer default when blank" data-testid="quotation-payment-terms-input" />
          </div>
          <div>
            <Label>Delivery terms</Label>
            <Input value={form.delivery_terms} onChange={(event) => setField('delivery_terms', event.target.value)}
              data-testid="quotation-delivery-terms-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Terms & conditions</Label>
            <Textarea rows={3} value={form.terms_conditions}
              onChange={(event) => setField('terms_conditions', event.target.value)}
              placeholder="Customer default when blank" data-testid="quotation-terms-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quotation-dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="quotation-dialog-submit">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileOutput className="mr-2 h-4 w-4" />}
            Create draft quotation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
